'use client';

import { MoveDownRight } from 'lucide-react';
import { m, useReducedMotion } from 'motion/react';
import { useAccordionContext } from './accordion-context';
import { Heading, type THeading } from '../heading/heading';
import { easings } from '@/utils/animation/config';

type TAccordion = {
  id: string;
  title: string;
  titleAs?: THeading['variant'];
  titleHighlight?: string;
  description: string;
};

export const Accordion = ({ id, title, titleAs = 'span', titleHighlight, description }: TAccordion) => {
  const { activeAccordion, setActiveAccordion } = useAccordionContext();
  const isActive = activeAccordion === id;
  const prefersReducedMotion = useReducedMotion();

  const toggleActive = () => {
    setActiveAccordion(isActive ? null : id);
  };

  return (
    <div
      className={`relative bg-backgroundLight border rounded-xl shadow-sm transition-all duration-300 overflow-hidden ${
        isActive
          ? 'border-action/50 shadow-[0_4px_20px_-4px_rgba(251,192,11,0.15)]'
          : 'border-neutral-800 hover:border-neutral-700'
      }`}
    >
      {/* Left accent bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-0.5 bg-action transition-all duration-300 ${
          isActive ? 'opacity-100' : 'opacity-0'
        }`}
      />

      <button className="w-full flex flex-row items-center justify-between gap-4 px-8 py-6" onClick={toggleActive}>
        <Heading highlight={titleHighlight} variant={titleAs} className="text-lg md:text-xl text-left tracking-wider">
          {title}
        </Heading>
        <m.span
          initial={false}
          animate={{ rotate: isActive ? -90 : 0 }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : { duration: 0.3, ease: easings.automotive }
          }
          className="shrink-0 w-5"
        >
          <MoveDownRight size="100%" color={isActive ? '#FBC00B' : '#fff'} />
        </m.span>
      </button>

      <m.div
        initial={false}
        animate={{
          height: isActive ? 'auto' : 0,
        }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : {
                height: { duration: 0.3, ease: easings.automotive },
              }
        }
        style={{ overflow: 'hidden' }}
      >
        <div className="px-8 pb-6">
          <div className="h-px bg-neutral-800 mb-4" />
          <p className="text-sm text-neutral-300">{description}</p>
        </div>
      </m.div>
    </div>
  );
};
